import { useEffect, useRef, useState } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'

import type { Schemas } from '@npc/api-client'

import { X } from 'lucide-react'

import { AiAccessIcon, ScopeLabel } from './scope'
import { clockCN } from './item'
import { humanBytes } from './capture'
import { cn } from '../../components/cn'
import {
  fetchMemo,
  unlockMemo,
  useDeleteMemoAttachment,
  useMemoReads,
  useUpdateMemo,
  type MemoUpdate,
} from '../../app/queries'
import { hhmm, shortDate, daysBetween } from '../../app/format'
import { t } from '../../copy'
import { T } from '../../copy/text'

/**
 * 详情弹框 `MemoDetail`（S09/S11/S12 的内容，壳按用户定稿改居中）。
 *
 * 设计稿原画的是右侧抽屉；用户定稿改成**居中弹框**：最宽 680px、
 * 高不过 86vh 内容在框内滚、遮罩与点遮罩/Esc 关闭都按对话框来。
 * 结构不变：标题+元信息 → 附件 → 正文（--well 底，pre-wrap）→
 * 作用域和 AI → 操作（编辑 / 移动 / 归档）。删除不在这条操作栏——
 * 这个平台对用户自己的东西没有硬删的权力，归档就是那把「收起来」。
 *
 * ## AI 权限控件带范围名
 *
 * 「AI 可读」必须跟范围名一起出现（SPEC-scope-ai §2.2）：
 * 杂项是「只有全局 Lulu」，项目内是「这个项目的 Claude 和 Grok」。
 * 单写「允许 AI 读取」是禁的。
 *
 * ## 版本冲突（S12）
 *
 * 编辑带着 version 提交，409 时把两个版本摆出来，默认「两边都留」。
 * 选之前本机那份一直留着，不自动覆盖。
 */
export function MemoDetailDrawer(props: {
  memo: Schemas['Memo']
  projectName: string
  onClose: () => void
  onMove: (memo: Schemas['Memo']) => void
  onArchive: (memo: Schemas['Memo']) => void
}) {
  const { memo } = props
  const update = useUpdateMemo()
  const removeAttachment = useDeleteMemoAttachment()
  const [access, setAccess] = useState(memo.ai_access)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(memo.body)
  const [conflict, setConflict] = useState<{ mine: string; server: Schemas['Memo'] } | null>(null)
  // 密码（用户需求：单条可设，默认不设）。unlocked 是解锁后的完整一条；
  // pwInput 是锁屏输入；pwBusy/pwError 管锁屏状态；manage 管「设/改/去密码」小表单。
  const locked = memo.has_password
  const [unlocked, setUnlocked] = useState<Schemas['Memo'] | null>(null)
  const [pwInput, setPwInput] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwError, setPwError] = useState<string | null>(null)
  const [manage, setManage] = useState<'idle' | 'set'>('idle')
  const [manageInput, setManageInput] = useState('')
  const [manageNote, setManageNote] = useState<string | null>(null)
  /** 图片灯箱（R2-2）：点 320px 预览放大看，点遮罩/Esc 关。 */
  const [lightbox, setLightbox] = useState<Schemas['MemoAttachment'] | null>(null)
  /**
   * 图片显示地址（巡检修复）：锁着的条目附件下载要密码头，<img src> 发不了
   * 头——解锁后用已验证的密码 fetch 换 blob URL；未锁的直接用原地址。
   */
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({})
  useEffect(() => {
    const imgs = ((unlocked ?? memo).attachments ?? []).filter((a) => a.mime.startsWith('image/'))
    let alive = true
    if (!memo.has_password) {
      setImageUrls(Object.fromEntries(imgs.map((a) => [a.id, a.url])))
      return
    }
    if (!unlocked || !pwRef.current) {
      setImageUrls({})
      return
    }
    void Promise.all(
      imgs.map(async (a) => {
        const res = await fetch(a.url, { headers: { 'X-Memo-Password': pwRef.current! } })
        if (!res.ok) throw new Error(`图片取回失败（${res.status}）`)
        return [a.id, URL.createObjectURL(await res.blob())] as const
      }),
    )
      .then((entries) => {
        if (alive) setImageUrls(Object.fromEntries(entries))
      })
      .catch(() => {
        // 取不回就不显示预览——文件名与下载入口还在
      })
    return () => {
      alive = false
    }
  }, [memo.id, memo.has_password, unlocked])
  /** 解锁用的密码留在 ref：输入框清了，附件下载的头还要带它。 */
  const pwRef = useRef<string | null>(null)

  // 锁屏与密码小表单只在**换了一条**时重置。不能挂在 body 变化上：
  // 自己刚设完密码，列表刷新会把这条的 body 换成空（锁位）——
  // 那一下不该把解锁态冲掉，否则设完密码自己反而看不见自己那条。
  useEffect(() => {
    setUnlocked(null)
    setPwInput('')
    setPwError(null)
    pwRef.current = null
    setManage('idle')
    setManageInput('')
    setManageNote(null)
  }, [memo.id])

  useEffect(() => {
    setAccess(memo.ai_access)
    setDraft(memo.body)
    setEditing(false)
    setConflict(null)
  }, [memo.id, memo.ai_access, memo.body])

  // Esc 关框：对话框的标准脾气（原生事件，直接读 isComposing）。
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !event.isComposing) {
        // 灯箱开着先关灯箱，不连带关掉整个详情。
        // 副作用不放进 setState updater（StrictMode 双调用会触发两次）
        if (lightbox) setLightbox(null)
        else props.onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [props.onClose, lightbox])

  /**
   * 取服务器上最新的一份**完整内容**。锁着的条目走普通 GET 只会拿到
   * 重写过的空正文——版本冲突面板那半边会变成空串，「两边都留」就把
   * 服务器的内容丢了。锁着的用已验证的密码再 unlock 一次。
   */
  async function fetchFresh(): Promise<Schemas['Memo']> {
    if (memo.has_password && pwRef.current) {
      return unlockMemo(memo.id, pwRef.current)
    }
    return fetchMemo(memo.id)
  }

  // 读过的记录：抽屉开着才取（次级数据不配常驻请求）。
  const reads = useMemoReads(memo.id, true)
  const readsItems = reads.data?.items ?? []

  async function doUnlock() {
    if (!pwInput || pwBusy) return
    setPwBusy(true)
    setPwError(null)
    try {
      const full = await unlockMemo(memo.id, pwInput)
      setUnlocked(full)
      pwRef.current = pwInput
      setPwInput('')
    } catch (error) {
      setPwError(error instanceof Error ? error.message : '')
    } finally {
      setPwBusy(false)
    }
  }

  /** 附件下载：设了密码的条目，字节也要密码——fetch 带头换 blob 再存。 */
  async function downloadAttachment(attachment: Schemas['MemoAttachment']) {
    try {
      const response = await fetch(attachment.url, {
        headers: unlocked && pwRef.current ? { 'X-Memo-Password': pwRef.current } : undefined,
      })
      if (!response.ok) throw new Error(`下载失败（${response.status}）`)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.name
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // 下载失败不拦界面——按钮还在，可以再点
    }
  }

  /** 设/改密码。成功后列表会刷新成锁位——把解锁态也带上，免得自己刚锁的条目自己看不见。 */
  async function savePassword(next: string | null) {
    try {
      const updated = await update.mutateAsync({
        memoId: memo.id,
        password: next,
      })
      if (next === null) {
        setManageNote(t('memo.password.removed'))
        setUnlocked(null)
      } else {
        setManageNote(t('memo.password.set.ok'))
        // 刚设完密码：列表里已是锁位；这里保持解锁态，继续看得到正文
        setUnlocked({ ...memo, ...updated, has_password: true })
      }
      setManage('idle')
      setManageInput('')
    } catch {
      // 错误由 mutation 层人话抛出；这里不清表单，让用户重试
    }
  }

  async function saveEdit() {
    if (draft.trim() === memo.body) {
      setEditing(false)
      return
    }
    try {
      await update.mutateAsync({
        memoId: memo.id,
        body: draft.trim(),
        expectedVersion: memo.version,
      })
      // 解锁快照也要跟上，否则正文显示的还是改之前的（渲染优先用快照）
      setUnlocked((prev) => (prev ? { ...prev, body: draft.trim() } : prev))
      setEditing(false)
      setConflict(null)
    } catch (error) {
      if (isVersionConflict(error)) {
        // 409：两个版本都在。拿服务器上最新那份摆出来（S12）。
        try {
          const fresh = await fetchFresh()
          setConflict({ mine: draft.trim(), server: fresh })
        } catch {
          setConflict({ mine: draft.trim(), server: memo })
        }
      }
    }
  }

  async function resolveConflict(mode: 'both' | 'mine' | 'server') {
    if (!conflict) return
    const serverMemo = conflict.server
    const body =
      mode === 'mine'
        ? conflict.mine
        : mode === 'both'
          ? `${serverMemo.body}\n\n${conflict.mine}`
          : serverMemo.body
    try {
      await update.mutateAsync({
        memoId: memo.id,
        body,
        expectedVersion: serverMemo.version,
      })
      setUnlocked((prev) => (prev ? { ...prev, body } : prev))
setConflict(null)
      setEditing(false)
      setDraft(body)
    } catch (error) {
      if (isVersionConflict(error)) {
        const fresh = await fetchFresh().catch(() => serverMemo)
        setConflict({ mine: body, server: fresh })
      }
    }
  }

  return (
    <>
      {/* 遮罩按设计稿对话框那一档（S16 确认层同款，值在下面类里）：
          详情从右侧抽屉改成居中弹框是用户定稿，遮罩跟着对话框走 */} {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
      <button
        type="button"
        aria-label={t('ui.close')}
        onClick={props.onClose}
        data-testid="memo-detail-overlay"
        className="fixed inset-0 z-40 cursor-default bg-[rgba(0,0,0,0.5)]"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
      />
      {/* 居中弹框（用户定稿，替代右侧抽屉）：最宽 680px、高不过 86vh，
          内容在框内滚；外层容器放行指针事件，点遮罩能关 */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-[16px]">
        <aside
          role="dialog"
          aria-modal="true"
          data-testid="memo-detail"
          className="pointer-events-auto flex max-h-[86vh] w-full max-w-[680px] flex-col overflow-hidden rounded-[12px] border border-m-line2 bg-m-bg1 shadow-[var(--m-pop)] max-[700px]:flex max-[700px]:h-[100dvh] max-[700px]:max-h-none max-[700px]:rounded-none max-[700px]:border-0"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        >
        {/* 标题 + 元信息 */}
        <header className="flex items-start gap-[12px] border-b border-m-line1 px-[18px] py-[14px]">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[20px] font-semibold text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {memo.body
                ? (memo.body.split('\n')[0] ?? '').slice(0, 40)
                : t('memo.detail.voice.title', { duration: clockCN(memo.audio?.duration_ms ?? 0) })}
            </h2>
            <p className="m-0 mt-[5px] text-[14px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {t('memo.detail.written', { date: when(memo.created_at) })}
              {' · '}
              {t('memo.detail.edited', { date: when(memo.updated_at ?? memo.created_at) })}
              {' · '}
              {memo.audio ? '语音' : memo.author === 'ai' ? '来自 Lulu' : '手写'}
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            aria-label={t('ui.close')}
            className="shrink-0 cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 p-[5px] text-m-t3 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            <X size={14} aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-[14px]">
          {locked && !unlocked ? (
            /* 锁屏：标题元信息可见（在页头），内容要密码。操作栏也不出——
               看不见内容就不该顺手归档/移动它 */
            <div data-testid="memo-lock" className="py-[24px]">
              <h3 className="m-0 text-[18px] font-semibold text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.lock.title" />
              </h3>
              <p className="m-0 mt-[6px] text-[15px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.lock.desc" />
              </p>
              <div className="mt-[14px] flex flex-wrap items-center gap-[8px]">
                <input
                  type="password"
                  // 不是登录框：单条备忘的加密锁。不标这个，浏览器的密码
                  // 管理器会自动填保存的登录密码，还把配对邮箱就近塞进
                  // 底下列表的搜索框（2026-09-07 生产实况）。
                  autoComplete="new-password"
                  value={pwInput}
                  onChange={(event) => setPwInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                      event.preventDefault()
                      void doUnlock()
                    }
                  }}
                  placeholder={t('memo.lock.input')}
                  aria-label={t('memo.lock.input')}
                  data-testid="memo-lock-input"
                  autoFocus
                  className="h-[38px] min-w-[220px] flex-1 rounded-[8px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[14px] text-m-t1 outline-none placeholder:text-m-t4"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                />
                <button
                  type="button"
                  onClick={() => void doUnlock()}
                  disabled={!pwInput || pwBusy}
                  data-testid="memo-lock-unlock"
                  className={cn(
                    'h-[34px] cursor-pointer rounded-[8px] border-none px-[14px] font-[inherit] text-[13px] font-medium',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                    !pwInput || pwBusy
                      ? 'cursor-not-allowed bg-m-bg3 text-m-t4'
                      : 'bg-m-pri text-m-pri-t',
                  )}
                >
                  <T id={pwBusy ? 'memo.capture.saving' : 'memo.lock.unlock'} />
                </button>
              </div>
              {pwError ? (
                <p role="alert" className="mb-0 mt-[10px] text-[14.5px] text-m-bad-t" data-testid="memo-lock-error"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  {pwError}
                </p>
              ) : null}
            </div>
          ) : editing ? (
            <div>
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                data-testid="memo-detail-edit"
                className="min-h-[120px] w-full resize-y rounded-npc-card border border-m-line2 bg-m-well p-[12px] font-[inherit] text-[18px] leading-[1.85] text-m-t1 outline-none"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
              />
              <div className="mt-[8px] flex items-center gap-[8px]">
                <button
                  type="button"
                  onClick={() => void saveEdit()}
                  disabled={update.isPending}
                  data-testid="memo-detail-save"
                  className="h-[28px] cursor-pointer rounded-[6px] border-none bg-m-pri px-[14px] font-[inherit] text-[12.5px] font-medium text-m-pri-t disabled:opacity-50"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                >
                  <T id="memo.capture.save" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false)
                    setDraft(memo.body)
                    setConflict(null)
                  }}
                  className="h-[28px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[12px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                >
                  <T id="ui.cancel" />
                </button>
              </div>

              {/* 版本冲突（S12）：两个版本都在，选一个；默认两边都留 */}
              {conflict ? (
                <section
                  data-testid="memo-conflict"
                  className="mt-[12px] rounded-npc-card border border-m-wn-t/35 bg-m-wn-s p-[12px]"
                >
                  <h3 className="m-0 text-[12.5px] font-semibold text-m-wn-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                    <T id="memo.conflict.title" />
                  </h3>
                  <p className="m-0 mt-[4px] text-[11.5px] text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                    <T id="memo.conflict.body" />
                  </p>
                  <div className="mt-[10px] grid gap-[10px] min-[560px]:grid-cols-2">
                    <ConflictPane
                      title={t('memo.conflict.mine')}
                      badge={t('memo.conflict.mine.v')}
                      body={conflict.mine}
                    />
                    <ConflictPane
                      title={t('memo.conflict.server')}
                      badge={t('memo.conflict.server.v')}
                      body={conflict.server.body}
                    />
                  </div>
                  <ul className="m-0 mt-[10px] list-none p-0">
                    {(['both', 'mine', 'server'] as const).map((mode, index) => (
                      <li key={mode}>
                        <label className="flex cursor-pointer items-start gap-[8px] py-[5px]">
                          <input
                            type="radio"
                            name="memo-conflict-choice"
                            defaultChecked={index === 0}
                            className="mt-[3px]"
                          />
                          <span>
                            <span className="block text-[12.5px] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                              <T id={`memo.conflict.option.${mode}`} />
                            </span>
                            <span className="block text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                              <T id={`memo.conflict.option.${mode}.desc`} />
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-[8px] flex items-center gap-[8px]">
                    <button
                      type="button"
                      onClick={() => {
                        const choice = document.querySelector<HTMLInputElement>(
                          'input[name="memo-conflict-choice"]:checked',
                        )
                        void resolveConflict((choice?.value as 'both' | 'mine' | 'server') ?? 'both')
                      }}
                      data-testid="memo-conflict-resolve"
                      className="h-[28px] cursor-pointer rounded-[6px] border-none bg-m-pri px-[14px] font-[inherit] text-[12.5px] font-medium text-m-pri-t"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                    >
                      <T id="memo.conflict.option.both" />
                    </button>
                    <span className="text-[11px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                      <T id="memo.conflict.foot" />
                    </span>
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <div className="min-w-0 max-w-full overflow-hidden rounded-npc-card bg-m-well p-[14px]">
              {((unlocked ?? memo).body ?? '') ? (
                <p
                  className="m-0 min-w-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] [word-break:break-word] text-[18px] leading-[1.85] text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  data-testid="memo-detail-body"
                >
                  {(unlocked ?? memo).body ?? ''}
                </p>
              ) : (
                <p className="m-0 min-w-0 max-w-full [overflow-wrap:anywhere] [word-break:break-word] text-[15px] leading-[1.8] text-m-t3" data-testid="memo-detail-body"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  {t('memo.detail.voice.title', {
                    duration: clockCN(memo.audio?.duration_ms ?? 0),
                  })}
                  {' —— '}
                  <T id="memo.voice.transcript.failed" />
                </p>
              )}
              {/* 补写（R2-1）：转写还没接（ASR key 未申请）期间的官方出路——补几个字就能搜到 */}
              {memo.audio && !(unlocked ?? memo).body && !editing ? (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  data-testid="memo-voice-selfwrite"
                  className="mt-[10px] h-[34px] cursor-pointer rounded-[8px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[14px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                >
                  <T id="memo.voice.selfwrite" />
                </button>
              ) : null}
            </div>
          )}

          {/* 附件（SPEC-components §8）：只列查得到的（= done）。删除入口在这，删的是文件不是条目。 */}
          {((unlocked ?? memo).attachments ?? []).length > 0 ? (
            <section className="mt-[16px]" data-testid="memo-detail-files">
              <h3 className="m-0 mb-[8px] text-[12px] font-semibold text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.detail.group.files" />
              </h3>
              <ul className="m-0 flex list-none flex-col gap-[6px] p-0">
                {((unlocked ?? memo).attachments ?? []).map((attachment) => (
                  <li
                    key={attachment.id}
                    data-testid="memo-detail-file"
                    className="flex min-w-0 flex-col gap-[8px] rounded-[7px] border border-m-line2 bg-m-bg1 px-[10px] py-[9px]"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  >
                    {attachment.mime.startsWith('image/') ? (
                      <button
                        type="button"
                        onClick={() => setLightbox(attachment)}
                        aria-label={t('memo.image.preview')}
                        data-testid="memo-detail-image"
                        className="cursor-pointer self-start border-none bg-transparent p-0"
                      >
                        {imageUrls[attachment.id] ? (
                          <img
                            src={imageUrls[attachment.id]}
                            alt={attachment.name}
                            loading="lazy"
                            className="max-w-[320px] rounded-[6px] border border-m-line2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                          />
                        ) : null}
                      </button>
                    ) : null}
                    <div className="flex min-w-0 items-center gap-[8px]">
                    <button
                      type="button"
                      onClick={() => void downloadAttachment(attachment)}
                      className="min-w-0 flex-1 cursor-pointer truncate border-none bg-transparent p-0 text-left text-[14.5px] text-m-t1 underline decoration-m-line3 underline-offset-2 hover:text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                      data-testid="memo-detail-file-download"
                    >
                      {attachment.name}
                    </button>
                    <span className="shrink-0 font-npc-mono text-[10.5px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                      {humanBytes(attachment.bytes)}
                    </span>
                    <button
                      type="button"
                      aria-label={t('memo.clip.remove')}
                      onClick={async () => {
                        try {
                          await removeAttachment.mutateAsync({ memoId: memo.id, attachmentId: attachment.id })
                        } catch {
                          // 失败时列表不会少这一行——留着就是如实
                        }
                      }}
                      disabled={removeAttachment.isPending}
                      data-testid="memo-detail-file-remove"
                      className="h-[24px] shrink-0 cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[8px] font-[inherit] text-[11px] text-m-t4 hover:text-m-t2 disabled:opacity-50"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                    >
                      <T id="memo.clip.remove" />
                    </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* 密码（用户需求：可设可不设，默认不设）。锁着未解锁时不显示——
              先看见内容，才谈得上给它设锁 */}
          {!locked || unlocked ? (
            <section className="mt-[16px]" data-testid="memo-password">
              <h3 className="m-0 mb-[8px] text-[15px] font-semibold text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.password.group" />
              </h3>
              {manageNote ? (
                <p className="m-0 mb-[8px] text-[14.5px] text-m-ok-t" data-testid="memo-password-note"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  {manageNote}
                </p>
              ) : null}
              {manage === 'idle' ? (
                <div className="flex flex-wrap items-center gap-[8px]">
                  {locked ? (
                    <>
                      <span className="text-[14.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                        <T id="memo.lock.title" />
                      </span>
                      <button
                        type="button"
                        onClick={() => { setManage('set'); setManageNote(null) }}
                        className="h-[30px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                        data-testid="memo-password-change"
                      >
                        <T id="memo.password.change" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(t('memo.password.confirm.remove'))) void savePassword(null)
                        }}
                        className="h-[30px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                        data-testid="memo-password-remove"
                      >
                        <T id="memo.password.remove" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-[14.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                        <T id="memo.password.none" />
                      </span>
                      <button
                        type="button"
                        onClick={() => { setManage('set'); setManageNote(null) }}
                        className="h-[30px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                        data-testid="memo-password-set"
                      >
                        <T id="memo.password.set" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-[8px]">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={manageInput}
                    onChange={(event) => setManageInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                        event.preventDefault()
                        if (manageInput.length >= 4) void savePassword(manageInput)
                      }
                    }}
                    placeholder={t('memo.password.input')}
                    aria-label={t('memo.password.input')}
                    data-testid="memo-password-input"
                    autoFocus
                    className="h-[32px] min-w-[200px] flex-1 rounded-[8px] border border-m-line2 bg-m-bg1 px-[10px] font-[inherit] text-[13px] text-m-t1 outline-none placeholder:text-m-t4"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  />
                  <button
                    type="button"
                    onClick={() => void savePassword(manageInput)}
                    disabled={manageInput.length < 4 || update.isPending}
                    data-testid="memo-password-save"
                    className={cn(
                      'h-[32px] cursor-pointer rounded-[8px] border-none px-[12px] font-[inherit] text-[12.5px] font-medium',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                      manageInput.length < 4 || update.isPending
                        ? 'cursor-not-allowed bg-m-bg3 text-m-t4'
                        : 'bg-m-pri text-m-pri-t',
                    )}
                  >
                    <T id="memo.capture.save" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setManage('idle'); setManageInput('') }}
                    className="h-[32px] cursor-pointer rounded-[8px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[12px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  >
                    <T id="ui.cancel" />
                  </button>
                </div>
              )}
            </section>
          ) : null}

          {/* 作用域和 AI（k 列 108px） */}
          <section className="mt-[16px]" data-testid="memo-detail-scope-group">
            <h3 className="m-0 mb-[8px] text-[12px] font-semibold text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.detail.group.scope" />
            </h3>
            <dl className="m-0 grid grid-cols-[108px_1fr] gap-x-[10px] gap-y-[7px]">
              <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.detail.where" />
              </dt>
              <dd className="m-0 flex items-center gap-[6px] text-[12.5px] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <ScopeLabel
                  kind={memo.project_id ? { project: props.projectName } : 'misc'}
                  data-testid="memo-detail-scope"
                />
              </dd>

              <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.detail.ai" />
              </dt>
              <dd className="m-0" data-testid="memo-detail-ai">
                <AiAccessControl
                  value={access}
                  inProject={!!memo.project_id}
                  disabled={update.isPending}
                  onChange={async (next) => {
                    setAccess(next)
                    try {
                      await update.mutateAsync({
                        memoId: memo.id,
                        aiAccess: next,
                        expectedVersion: memo.version ?? 1,
                      })
                    } catch {
                      setAccess(memo.ai_access)
                    }
                  }}
                />
              </dd>

              <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.detail.ai.reads" />
              </dt>
              <dd className="m-0 text-[12px] text-m-t2" data-testid="memo-detail-reads"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                {reads.isPending ? (
                  <T id="memo.loading" />
                ) : readsItems.length === 0 ? (
                  <T id="memo.detail.ai.reads.none" />
                ) : (
                  readsItems
                    .slice(0, 3)
                    .map(
                      (r) =>
                        `${r.caller} ${daysBetween(r.read_at) === 0 ? hhmm(r.read_at) : shortDate(r.read_at)}`,
                    )
                    .join(' · ')
                )}
              </dd>

              <dt className="text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.detail.other.projects" />
              </dt>
              <dd className="m-0 text-[14.5px] text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.detail.cant.read" />
              </dd>
            </dl>
          </section>
        </div>

        {/* 底栏：操作。锁着未解锁时不出——看不见内容，就不该顺手归档/移动它 */}
        {locked && !unlocked ? null : (
        <footer className="flex flex-wrap items-center gap-[8px] border-t border-m-line1 px-[18px] py-[10px]">
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="h-[28px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            data-testid="memo-detail-edit-toggle"
          >
            <T id="ui.edit" />
          </button>
          <button
            type="button"
            onClick={() => props.onMove(memo)}
            className="h-[28px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            data-testid="memo-detail-move"
          >
            <T id="memo.detail.actions.move" />
          </button>
          <button
            type="button"
            onClick={() => props.onArchive(memo)}
            className="h-[28px] cursor-pointer rounded-[6px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            data-testid="memo-detail-archive"
          >
            <T id="memo.detail.actions.archive" />
          </button>
          {memo.archived_at ? (
            <span className="text-[11.5px] text-m-t3" data-testid="memo-detail-archived-note"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {t('memo.archive.archived.at', {
                date: shortDate(memo.archived_at),
              })}
            </span>
          ) : null}
        </footer>
        )}

        {/* 图片灯箱（R2-2）：点大图开，点遮罩/Esc 关 */}
        {lightbox ? (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('memo.image.preview')}
            data-testid="memo-lightbox"
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] flex cursor-zoom-out items-center justify-center bg-[rgba(0,0,0,0.72)] p-[24px]"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            <img
              src={imageUrls[lightbox.id] ?? lightbox.url}
              alt={lightbox.name}
              onClick={(event) => event.stopPropagation()}
              className="max-h-[82vh] max-w-[90vw] rounded-[8px] object-contain"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            />
          </div>
        ) : null}
        </aside>
      </div>
    </>
  )
}

/** 冲突两栏的一边。 */
function ConflictPane(props: { title: string; badge: string; body: string }) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden rounded-npc-card border border-m-line2 bg-m-bg1 p-[10px]">
      <p className="m-0 flex items-center gap-[6px] text-[11.5px] font-medium text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
        {props.title}
        <span className="rounded-[5px] bg-m-bg3 px-[5px] py-[1px] text-[10.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {props.badge}
        </span>
      </p>
      <p className="m-0 mt-[6px] min-w-0 max-w-full line-clamp-5 whitespace-pre-wrap [overflow-wrap:anywhere] [word-break:break-word] text-[12px] leading-[1.6] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
        {props.body}
      </p>
    </div>
  )
}

/**
 * 三态单选（SPEC-components §4）。每态一行：图标 + 文案 + 范围名 +
 * 一句行为说明。`shared` 的范围名按这条备忘的作用域给。
 */
export function AiAccessControl(props: {
  value: 'default' | 'shared' | 'hidden'
  inProject: boolean
  disabled?: boolean
  onChange: (next: 'default' | 'shared' | 'hidden') => void
}) {
  const rows = [
    {
      key: 'default' as const,
      label: t('memo.ai.default'),
      desc: t('memo.ai.default.desc'),
    },
    {
      key: 'shared' as const,
      label: props.inProject ? t('memo.ai.shared.scope.project') : t('memo.ai.shared.scope.global'),
      desc: props.inProject
        ? t('memo.ai.shared.scope.project.desc')
        : t('memo.ai.shared.scope.global.desc'),
    },
    {
      key: 'hidden' as const,
      label: t('memo.ai.hidden'),
      desc: t('memo.ai.hidden.desc'),
    },
  ]

  return (
    <div className="flex flex-col gap-[4px]" role="radiogroup" aria-label={t('memo.ai.change')}>
      {rows.map((row) => (
        <label
          key={row.key}
          className={cn(
            'flex cursor-pointer items-start gap-[8px] rounded-[8px] border p-[8px]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
            props.value === row.key
              ? 'border-m-line2 bg-m-bg2'
              : 'border-transparent hover:bg-m-bg2',
            props.disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <input
            type="radio"
            name={`memo-ai-${props.inProject ? 'p' : 'g'}`}
            checked={props.value === row.key}
            disabled={props.disabled}
            onChange={() => props.onChange(row.key)}
            className="mt-[2px]"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-[6px] text-[12.5px] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <AiAccessIcon access={row.key} />
              {row.label}
            </span>
            <span className="mt-[2px] block text-[11px] leading-[1.5] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              {row.desc}
            </span>
          </span>
        </label>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------- 小工具

function when(iso: string): string {
  return daysBetween(iso) === 0 ? hhmm(iso) : `${shortDate(iso)} ${hhmm(iso)}`
}

/** 409 判定：版本冲突的错误码是 memo 版本冲突那一条。 */
function isVersionConflict(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes('这条别人刚改过') || error.message.includes('409'))
  )
}



export type { UseMutationResult, MemoUpdate }
